<template lang="pug">
v-menu(
  ref="menu"
  v-model="previewOpen"
  :open-on-click="false"
  :close-on-content-click="false"
  :open-on-hover="false"
  :max-width="360"
  transition="fade-transition"
  :disabled="!truncate || tableCellHover.state.suspended"
  :content-class="previewContentClass"
  @input="onPreviewInput"
)
  template(#activator="{ attrs }")
    div.table-cell(
      ref="cell"
      v-bind="attrs"
      :class="{ 'table-cell--truncate': truncate, 'table-cell--active': interactionVisible, 'table-cell--linked': !!linkTo }"
      :tabindex="truncate && !linkTo ? 0 : null"
      @mouseenter="onMouseenter"
      @mousemove="onMousemove"
      @mouseleave="onMouseleave"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
      @keydown.esc.capture.stop="hideInteraction"
      @keydown.f2.stop.prevent="openPreview"
    )
      router-link(v-if="linkTo" :to="linkTo" custom v-slot="{ href, navigate }")
        a.table-cell__link(:href="href" @click="openLink($event, navigate)")
          div.table-cell__text(ref="text")
            slot
      div.table-cell__text(v-else ref="text")
        slot
      span.table-cell__copy(v-if="copyable && !previewOpen" v-show="!tableCellHover.state.suspended")
        CopyTextButton(:get-text="getText" :class="{ 'table-cell__copy--visible': interactionVisible }")
  v-card(
    v-if="fullText"
    tile
    :style="previewSurfaceStyle"
    @click.stop
    @keydown.esc.capture.stop="hideInteraction"
    @keydown.stop
    @wheel.native="onPreviewWheel"
    @focusout="onFocusOut"
    @mouseenter="onPreviewMouseenter"
    @mouseleave="onPreviewMouseleave"
    role="group"
    aria-label="Повний текст комірки"
  )
    div.table-cell-preview__content
      router-link(v-if="linkTo" :to="linkTo" custom v-slot="{ href, navigate }")
        a.table-cell-preview__text.table-cell-preview__link(
          :href="href"
          :style="previewTextStyle"
          @click="openLink($event, navigate)"
        ) {{ fullText }}
      div.table-cell-preview__text(v-else :style="previewTextStyle") {{ fullText }}
      CopyTextButton(v-if="copyable" :text="fullText").table-cell-preview__copy
</template>

<script>
import CopyTextButton from '@/components/CopyTextButton.vue'

const OPEN_DELAY = 300
const CLOSE_DELAY = 200

function scrollByDelta (element, property, delta) {
  const previous = element[property]
  element[property] += delta
  return delta - (element[property] - previous)
}

// A detached menu does not share the source cell's native scroll chain.
function scrollSourceAncestors (source, deltaX, deltaY) {
  const viewport = document.scrollingElement
  for (let element = source; element && (deltaX || deltaY); element = element.parentElement) {
    // The viewport is one scroll target, even when body and html both have scroll ranges.
    if ((element === document.body || element === document.documentElement) && element !== viewport) continue
    const style = window.getComputedStyle(element)
    const allowsScroll = overflow => element === viewport
      ? overflow !== 'hidden' && overflow !== 'clip' : /^(auto|scroll|overlay)$/.test(overflow)
    if (deltaX && allowsScroll(style.overflowX)) deltaX = scrollByDelta(element, 'scrollLeft', deltaX)
    if (deltaY && allowsScroll(style.overflowY)) deltaY = scrollByDelta(element, 'scrollTop', deltaY)
  }
}

export default {
  name: 'TableCell',
  components: { CopyTextButton },
  inject: {
    tableCellHover: {
      default: () => ({ state: { suspended: false }, resume: () => {}, suspend: () => {} })
    }
  },
  props: {
    copyable: { type: Boolean, default: true },
    truncate: { type: Boolean, default: true },
    linkTo: { type: [String, Object], default: null }
  },
  data () {
    return {
      previewOpen: false, interactionVisible: false, hovered: false, focused: false, fullText: '',
      previewSurfaceStyle: {}, previewTextStyle: {}, previewHovered: false
    }
  },
  computed: {
    previewContentClass () {
      return 'table-cell-preview' + (this.tableCellHover.state.suspended ? ' table-cell-preview--suspended' : '')
    }
  },
  watch: {
    'tableCellHover.state.suspended' (suspended) {
      if (!suspended) return
      this.hideInteraction()
      this.hovered = false
      this.focused = false
      this.previewHovered = false
    }
  },
  created () {
    this.openTimer = null
    this.closeTimer = null
  },
  beforeDestroy () { this.clearInteractionTimers() },
  methods: {
    clearInteractionTimers () {
      clearTimeout(this.openTimer)
      clearTimeout(this.closeTimer)
      this.openTimer = null
      this.closeTimer = null
    },
    showInteraction () {
      this.clearInteractionTimers()
      if (this.tableCellHover.state.suspended || (!this.hovered && !this.focused && !this.previewHovered)) return
      this.preparePreview()
      this.interactionVisible = true
      this.previewOpen = this.truncate && this.isOverflowing() && !!this.fullText
    },
    hideInteraction () {
      this.clearInteractionTimers()
      this.interactionVisible = false
      this.previewOpen = false
    },
    scheduleOpen () {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
      if (this.interactionVisible || this.focused) return this.showInteraction()
      if (!this.openTimer) this.openTimer = setTimeout(this.showInteraction, OPEN_DELAY)
    },
    scheduleClose () {
      clearTimeout(this.openTimer)
      this.openTimer = null
      if (this.hovered || this.focused || this.previewHovered) return
      clearTimeout(this.closeTimer)
      this.closeTimer = setTimeout(this.hideInteraction, CLOSE_DELAY)
    },
    getText () { return (this.$refs.text?.innerText || '').trim() },
    isOverflowing () {
      const text = this.$refs.text
      return !!text && [text, ...text.querySelectorAll('*')].some(element => element.scrollWidth > element.clientWidth + 1)
    },
    openPreview (event) {
      if (event) this.tableCellHover.resume(event)
      if (this.tableCellHover.state.suspended) return
      if (event?.type === 'keydown') this.focused = true
      this.showInteraction()
    },
    preparePreview () {
      this.fullText = this.getText()
      const cell = this.$refs.cell
      const text = this.$refs.text
      if (!cell || !text) return
      const style = window.getComputedStyle(text.firstElementChild || text)
      this.previewTextStyle = {
        fontFamily: style.fontFamily, fontSize: style.fontSize,
        fontWeight: style.fontWeight, lineHeight: style.lineHeight,
        letterSpacing: style.letterSpacing, color: style.color
      }
      // Blend a translucent row highlight over the table's opaque theme background.
      const table = cell.closest('.v-data-table')
      const row = cell.closest('tr')
      if (table && row) {
        const background = window.getComputedStyle(row).backgroundColor
        this.previewSurfaceStyle = {
          backgroundColor: window.getComputedStyle(table).backgroundColor,
          backgroundImage: `linear-gradient(${background}, ${background})`
        }
      }
    },
    onPreviewInput (open) {
      if (!open) return this.hideInteraction()
      if (this.tableCellHover.state.suspended || !this.interactionVisible) {
        this.$nextTick(() => { this.hideInteraction() })
      }
    },
    onMouseenter (event) {
      this.tableCellHover.resume(event)
      if (this.tableCellHover.state.suspended) return
      this.hovered = true
      this.scheduleOpen()
    },
    onMousemove (event) {
      this.tableCellHover.resume(event)
      if (!this.tableCellHover.state.suspended && !this.hovered) this.onMouseenter(event)
    },
    onMouseleave () {
      this.hovered = false
      this.scheduleClose()
    },
    onPreviewMouseenter () {
      this.previewHovered = true
      this.scheduleOpen()
    },
    onPreviewMouseleave () {
      this.previewHovered = false
      this.scheduleClose()
    },
    onFocusIn (event) {
      this.tableCellHover.resume(event)
      this.focused = event.target.matches(':focus-visible')
      if (this.focused) this.showInteraction()
    },
    onFocusOut (event) {
      const next = event.relatedTarget
      if (next && (this.$refs.cell.contains(next) || this.$refs.menu.$refs.content?.contains(next))) return
      this.focused = false
      this.scheduleClose()
    },
    onPreviewWheel (event) {
      if (event.ctrlKey || event.metaKey || event.defaultPrevented || !event.cancelable) return
      const cell = this.$refs.cell
      const wrapper = cell?.closest('.v-data-table__wrapper')
      if (!wrapper) return
      const lineHeight = parseFloat(window.getComputedStyle(cell).lineHeight) || 20
      const unitX = event.deltaMode === 1 ? lineHeight : event.deltaMode === 2 ? wrapper.clientWidth : 1
      const unitY = event.deltaMode === 1 ? lineHeight : event.deltaMode === 2 ? wrapper.clientHeight : 1
      let deltaX = event.deltaX * unitX
      let deltaY = event.deltaY * unitY
      if (event.shiftKey && !deltaX) {
        deltaX = deltaY
        deltaY = 0
      }
      if (!deltaX && !deltaY) return

      const text = event.target instanceof Element ? event.target.closest('.table-cell-preview__text') : null
      if (!deltaX && text) {
        const available = deltaY > 0 ? text.scrollHeight - text.clientHeight - text.scrollTop : text.scrollTop
        if (Math.abs(deltaY) <= available) return
      }

      event.preventDefault()
      if (!deltaX && text) deltaY = scrollByDelta(text, 'scrollTop', deltaY)
      this.tableCellHover.suspend()
      this.hideInteraction()
      this.hovered = false
      this.previewHovered = false
      scrollSourceAncestors(wrapper, deltaX, deltaY)
    },
    openLink (event, navigate) {
      if (event.defaultPrevented) return
      if (event.detail > 0 && window.getSelection()?.toString()) {
        event.preventDefault()
        return
      }
      navigate(event)
    }
  }
}
</script>

<style lang="sass">
.table-cell
  position: relative
  display: flex
  align-items: center
  gap: 4px
  min-width: 0
  max-width: 100%
  min-height: 40px
  padding: 4px 10px
  outline-offset: -2px

  &__text
    flex: 1 1 auto
    min-width: 0

  &--truncate &__text,
  &--truncate &__text > *
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__text > *
    max-width: 100%

  .v-chip
    max-width: 100%
    overflow: hidden

    .v-chip__content
      min-width: 0
      max-width: 100%
      overflow: hidden

      > span
        display: block
        min-width: 0
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap
        overflow-wrap: normal

  &__copy
    position: absolute
    right: 6px
    top: 50%
    transform: translateY(-50%)
    z-index: 2

    > .copy-text-button
      opacity: 0
      visibility: hidden
      pointer-events: none
      transition: opacity .12s ease, visibility 0s linear .12s

    > .table-cell__copy--visible
      opacity: 1
      visibility: visible
      pointer-events: auto
      transition-delay: 0s

  &--active &__text
    color: var(--v-primary-base, #2e8b57)

  &__link
    display: flex
    align-items: center
    min-width: 0
    width: 100%
    min-height: 32px
    color: inherit !important
    text-decoration: none

    .table-cell__text
      position: relative
      z-index: 1

    &::after
      content: ''
      position: absolute
      inset: 0
      z-index: 0

.table-cell-preview
  max-width: min(360px, calc(100vw - 24px)) !important
  border-radius: 0 !important
  box-shadow: 0 2px 8px rgba(0, 0, 0, .18) !important

  &.fade-transition-enter-active, &.fade-transition-leave-active
    transition: opacity .12s ease !important

  &.fade-transition-leave-active
    pointer-events: none

  &--suspended.fade-transition-enter-active, &--suspended.fade-transition-leave-active
    transition: none !important

  &__content
    display: flex
    align-items: center
    gap: 8px
    min-height: 40px
    padding: 4px 10px

  &__text
    flex: 1 1 auto
    min-width: 0
    white-space: pre-wrap
    overflow-wrap: anywhere
    max-height: 50vh
    overflow-y: auto
    user-select: text

  &__copy
    flex: 0 0 auto
    align-self: flex-start
    margin-top: 2px

  &__link
    color: inherit
    text-decoration: none

@media (hover: none)
  .table-cell__copy > .copy-text-button
    opacity: 1
    visibility: visible
    pointer-events: auto

@media (prefers-reduced-motion: reduce)
  .table-cell__copy > .copy-text-button,
  .table-cell-preview.fade-transition-enter-active,
  .table-cell-preview.fade-transition-leave-active
    transition: none !important
</style>
